import {
  hasGroceryPlatformData,
  platformDataToComparison,
} from "./groceryPlatformData";

const STORAGE_KEY = "femtyz_grocery_product_views";

export function normalizeGroceryProductPlatform(platform) {
  const p = String(platform || "").trim().toLowerCase();
  if (!p) return "";
  if (p.includes("tesco")) return "tesco";
  if (p.includes("asda")) return "asda";
  if (p.includes("morris")) return "morrison";
  if (p.includes("sains")) return "sainsbury";
  if (p === "tesco" || p === "asda" || p === "morrison" || p === "sainsbury") return p;
  return "";
}

function storageKey(listId, productId) {
  return `${String(listId)}:${String(productId)}`;
}

export function rememberGroceryProductView({ listId, productId, view, platform }) {
  if (!listId || !productId || !view) return;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const map = raw ? JSON.parse(raw) : {};
    map[storageKey(listId, productId)] = {
      view,
      platform: normalizeGroceryProductPlatform(platform) || undefined,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore
  }
}

export function getGroceryProductView({ listId, productId }) {
  if (!listId || !productId) return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const map = raw ? JSON.parse(raw) : {};
    return map[storageKey(listId, productId)] || null;
  } catch {
    return null;
  }
}

export function getGroceryListCatalogProductId(item) {
  const catalogId = item?.product_id ?? item?.pid ?? item?.sku;
  if (catalogId == null || String(catalogId).trim() === "") return null;
  return String(catalogId).trim();
}

function getGroceryPlatformData(item) {
  if (Array.isArray(item?.platform_data)) return item.platform_data;
  if (Array.isArray(item?.platformData)) return item.platformData;
  return [];
}

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value == null) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    return value;
  }
  return "";
}

function firstFinitePrice(...values) {
  for (const value of values) {
    if (value == null || value === "") continue;
    const n =
      typeof value === "number"
        ? value
        : Number.parseFloat(String(value).trim().replace(",", ".").replace(/[^0-9.-]/g, ""));
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

/** Build a product object for ProductDetail / ProductDetailSingle from a grocery list row. */
export function groceryListItemToNavProduct(item) {
  const catalogId = getGroceryListCatalogProductId(item);
  if (!catalogId) return null;

  const platform =
    normalizeGroceryProductPlatform(item?.platform) || String(item?.platform || "").trim();
  const platformData = getGroceryPlatformData(item);
  const comparison = platformDataToComparison(platformData);

  // Outer grocery row wins. Fall back to matching platform_data row, then any priced row.
  const matchingPlatformRow =
    comparison.find(
      (row) =>
        String(row?.product_id || "").trim() === catalogId ||
        String(row?.pid || "").trim() === catalogId
    ) ||
    comparison.find((row) => row.platform === platform) ||
    comparison.find((row) => Number.isFinite(row?.price) && row.price > 0) ||
    null;

  const title = firstNonEmpty(
    item?.product_name,
    item?.title,
    item?.name,
    matchingPlatformRow?.product_name,
    catalogId
  );
  const imageUrl = firstNonEmpty(item?.image_url, item?.image, matchingPlatformRow?.image);
  const description = firstNonEmpty(
    item?.description,
    matchingPlatformRow?.description
  );
  const brand = firstNonEmpty(item?.brand, matchingPlatformRow?.brand);
  const price = firstFinitePrice(
    item?.price,
    matchingPlatformRow?.price,
    matchingPlatformRow?.offer_price,
    item?.offer_price
  );
  const rating = firstFinitePrice(
    item?.rating,
    item?.average_rating,
    matchingPlatformRow?.rating,
    matchingPlatformRow?.average_rating
  );
  const reviewCount = firstFinitePrice(
    item?.review_count,
    matchingPlatformRow?.review_count
  );

  // Keep comparison for Where To Buy, but overlay outer product onto matching row.
  const enrichedComparison = comparison.map((row) => {
    const sameProductId = String(row?.product_id || "").trim() === catalogId;
    const samePlatform = row.platform === platform;
    if (!sameProductId && !samePlatform) return row;
    return {
      ...row,
      product_id: catalogId || row.product_id,
      product_name: firstNonEmpty(title, row.product_name),
      price: firstFinitePrice(item?.price, row.price, row.offer_price, item?.offer_price),
      url: firstNonEmpty(item?.url, row.url) || null,
      description: firstNonEmpty(description, row.description),
      brand: firstNonEmpty(brand, row.brand),
      match_type: firstNonEmpty(item?.match_type, row.match_type),
      average_rating: firstFinitePrice(rating, row.average_rating, row.rating),
      rating: firstFinitePrice(rating, row.rating, row.average_rating),
      review_count: firstFinitePrice(reviewCount, row.review_count),
    };
  });

  return {
    platform,
    pid: catalogId,
    product_id: catalogId,
    sku: catalogId,
    title,
    product_name: title,
    price,
    images: imageUrl ? [imageUrl] : [],
    image: imageUrl,
    image_url: imageUrl,
    packsize: firstNonEmpty(item?.weight, item?.packsize),
    weight: firstNonEmpty(item?.weight, item?.packsize),
    category: firstNonEmpty(item?.category, item?.category_name),
    subcategory: firstNonEmpty(item?.subcategory, item?.sub_category),
    brand,
    description,
    url: firstNonEmpty(item?.url, matchingPlatformRow?.url) || null,
    average_rating: rating,
    review_count: reviewCount,
    match_type: firstNonEmpty(item?.match_type, matchingPlatformRow?.match_type),
    platform_data: platformData,
    comparison: enrichedComparison,
    fromGrocery: true,
  };
}

export function getGroceryProductDetailPath({ listId, productId, platform, item }) {
  const id = String(productId || "").trim();
  if (!id) return null;

  // Prefer the 4-store compare detail page when DB has platform_data.
  if (hasGroceryPlatformData(getGroceryPlatformData(item))) {
    return `/product/${encodeURIComponent(id)}`;
  }

  const stored = getGroceryProductView({ listId, productId: id });
  const normalizedPlatform = normalizeGroceryProductPlatform(
    stored?.platform || platform || item?.platform
  );
  const view = stored?.view || (normalizedPlatform ? "single" : "compare");

  if (view === "single" && normalizedPlatform) {
    return `/product-store/${encodeURIComponent(normalizedPlatform)}/${encodeURIComponent(id)}`;
  }

  return `/product/${encodeURIComponent(id)}`;
}

export function getGroceryProductNavigation({ listId, item }) {
  const catalogId = getGroceryListCatalogProductId(item);
  if (!catalogId) return null;

  const product = groceryListItemToNavProduct(item);
  if (!product) return null;

  const path = getGroceryProductDetailPath({
    listId,
    productId: catalogId,
    platform: item?.platform,
    item,
  });
  if (!path) return null;

  return {
    path,
    state: {
      product,
      fromGrocery: true,
      pricingMode: "Base",
      offer: false,
    },
  };
}
